// src/pages/Admin/UrunlerPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Edit2, Trash2, X, Star, Search, ChevronDown, ChevronUp, Bold, Italic, Underline, List, Link2, Eraser, Download } from "lucide-react";
import Select, { GroupBase } from "react-select";
import { postRequest } from "../../../api/postRequest";
import { postFormRequest } from "../../../api/postFormRequest";
import Modal from "../../../components/Common/Modal/Modal";

/* =============================
   Tipler
============================= */
interface ApiResponse<T> {
  success: boolean;
  errorCode?: string;
  errorDetail?: string;
  data?: T;
}
interface Category {
  id: number;
  ad: string;
  ustKategoriId: number | null;
}
interface TreeCategory extends Category {
  children: TreeCategory[];
}
interface RowItem {
  cat: Category;
  depth: number;
}
interface CategoryOption {
  value: number;
  label: string;
  depth: number;
}

interface Product {
  id: number;
  kategoriId: number;
  ad: string;
  aciklama?: string;

  fiyat: number;
  paraBirimi: string;
  stokAdedi: number;
  fotoUrl?: string; // tek alan: düz URL veya JSON {list,main}
  fotoUrls?: string[];
  profilFotoUrl?: string;
  aktifMi: boolean;

  oneCikanmi?: boolean;

  listeFiyati?: number | null;
  indirimliFiyat?: number | null;
  indirimBaslangic?: string | null; // ISO string
  indirimBitis?: string | null; // ISO string
  alisFiyati?: number | null;
  kur?: number | null;

  indirimAktifMi?: boolean;
  satisFiyati?: number;
}

interface FeatureInput {
  id?: number;
  ozellikAdi: string;
  ozellikDegeri: string;
  siraNo: number;
}

interface PriceChartingResult {
  ad: string;
  fiyatUsd?: number | null;
  fiyatTl?: number | null;
  kur?: number | null;
  paraBirimi: string;
  fotoUrl?: string | null;
  tumFiyatlar?: Record<string, number | null>;
  uyarilar?: string[];
}

/* =============================
   Yardımcılar
============================= */
const parseFotoField = (singleField?: string) => {
  if (!singleField) return { list: [] as string[], main: "" as string };
  try {
    const obj = JSON.parse(singleField);
    if (obj && Array.isArray(obj.list)) {
      return { list: obj.list as string[], main: String(obj.main ?? "") };
    }
  } catch {
    /* düz URL */
  }
  return { list: [singleField], main: singleField };
};

const toDatetimeLocalValue = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocalValue = (v: string) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const safeNum = (v: any, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const formatMoney = (amount: number, currency: string) => `${amount} ${currency}`;

/* =============================
   Rich Text Editor (minimal & basit)
   - HTML saklar
============================= */
type RTEProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
};
const RichTextEditor: React.FC<RTEProps> = ({ value, onChange, placeholder }) => {
  const ref = useRef<HTMLDivElement>(null);

  const exec = (cmd: string, arg?: string) => {
    // execCommand hala birçok tarayıcıda çalışır; bu admin ekranı için yeterli.
    document.execCommand(cmd, false, arg);
    // command sonrası html al
    const html = ref.current?.innerHTML ?? "";
    onChange(html);
  };

  const askLink = () => {
    const url = window.prompt("Link adresi (https://...)");
    if (!url) return;
    exec("createLink", url);
  };

  useEffect(() => {
    // dışarıdan value set edildiğinde editöre yaz
    if (!ref.current) return;
    if ((ref.current.innerHTML || "") !== (value || "")) ref.current.innerHTML = value || "";
  }, [value]);

  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-gray-50">
        <button type="button" className="rteBtn" onClick={() => exec("bold")} title="Kalın">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" className="rteBtn" onClick={() => exec("italic")} title="İtalik">
          <Italic className="w-4 h-4" />
        </button>
        <button type="button" className="rteBtn" onClick={() => exec("underline")} title="Altı çizili">
          <Underline className="w-4 h-4" />
        </button>

        <span className="mx-1 h-5 w-px bg-gray-200" />

        <button type="button" className="rteBtn" onClick={() => exec("insertUnorderedList")} title="Madde işaretli liste">
          <List className="w-4 h-4" />
        </button>
        <button type="button" className="rteBtn" onClick={askLink} title="Link ekle">
          <Link2 className="w-4 h-4" />
        </button>

        <span className="mx-1 h-5 w-px bg-gray-200" />

        <button
          type="button"
          className="rteBtn text-red-600"
          onClick={() => {
            if (!ref.current) return;
            ref.current.innerHTML = "";
            onChange("");
          }}
          title="Temizle"
        >
          <Eraser className="w-4 h-4" />
        </button>

        <style>{`
          .rteBtn{
            display:inline-flex; align-items:center; justify-content:center;
            width:34px; height:34px; border-radius:10px;
            border:1px solid rgba(0,0,0,0.08);
            background:white;
          }
          .rteBtn:hover{ background:#f9fafb; }
        `}</style>
      </div>

      <div className="p-3">
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={() => onChange(ref.current?.innerHTML ?? "")}
          onBlur={() => onChange(ref.current?.innerHTML ?? "")}
          className="min-h-[120px] outline-none text-sm leading-relaxed prose prose-sm max-w-none"
          data-placeholder={placeholder || "Ürün açıklaması yazın…"}
          style={{
            // placeholder için
            position: "relative",
          }}
        />
        {/* Basit placeholder */}
        {!value?.trim() && (
          <div className="pointer-events-none -mt-[120px] min-h-[120px] text-sm text-gray-400 p-1">
            {placeholder || "Ürün açıklaması yazın… (kalın yazı, liste, link ekleyebilirsiniz)"}
          </div>
        )}
      </div>
    </div>
  );
};

/* =============================
   Basit Accordion
============================= */
const Section: React.FC<{
  title: string;
  desc?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, desc, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border bg-white">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
      >
        <div>
          <div className="font-semibold text-sm">{title}</div>
          {desc ? <div className="text-xs text-gray-500 mt-0.5">{desc}</div> : null}
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
      </button>
      {open ? <div className="px-4 pb-4 pt-0">{children}</div> : null}
    </div>
  );
};

/* =============================
   Sayfa
============================= */
const UrunlerPage: React.FC = () => {
  /* ------ KATEGORİLER ------ */
  const [categories, setCategories] = useState<Category[]>([]);
  const buildTree = (list: Category[]): TreeCategory[] => {
    const map = new Map<number, TreeCategory>();
    const roots: TreeCategory[] = [];
    list.forEach((c) => map.set(c.id, { ...c, children: [] }));
    map.forEach((node) => {
      if (node.ustKategoriId && map.has(node.ustKategoriId)) map.get(node.ustKategoriId)!.children.push(node);
      else roots.push(node);
    });
    return roots;
  };
  const flattenTree = (nodes: TreeCategory[], depth = 0): RowItem[] =>
    nodes.flatMap((n) => [{ cat: n, depth }, ...flattenTree(n.children, depth + 1)]);
  const categoryOptions: CategoryOption[] = flattenTree(buildTree(categories)).map(({ cat, depth }) => ({
    value: cat.id,
    label: cat.ad,
    depth,
  }));

  /* ------ ÜRÜNLER ------ */
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  /* ------ Basit Filtre ------ */
  const [q, setQ] = useState("");
  const filteredProducts = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return products;
    return products.filter((p) => (p.ad || "").toLowerCase().includes(s));
  }, [products, q]);

  /* ------ MODAL & FORM ------ */
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const saveBtnRef = useRef<HTMLElement>(null);

  /* ------ PriceCharting İçe Aktarma ------ */
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);

  const emptyForm: Partial<Product> = {
    kategoriId: 0,
    ad: "",
    aciklama: "",

    fiyat: 0,
    paraBirimi: "TRY",
    stokAdedi: 0,

    fotoUrl: "",
    fotoUrls: [],
    profilFotoUrl: "",
    aktifMi: true,

    oneCikanmi: false,

    listeFiyati: null,
    indirimliFiyat: null,
    indirimBaslangic: null,
    indirimBitis: null,
    alisFiyati: null,
    kur: null,
  };

  const [form, setForm] = useState<Partial<Product>>(emptyForm);
  const [features, setFeatures] = useState<FeatureInput[]>([]);

  /* ------ FOTOĞRAFLAR ------ */
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [existingUrls, setExistingUrls] = useState<string[]>([]);
  const [removedUrls, setRemovedUrls] = useState<string[]>([]);
  const [mainExistingUrl, setMainExistingUrl] = useState<string | null>(null);
  const [mainNewIndex, setMainNewIndex] = useState<number | null>(null);

  /* ------ API ------ */
  const fetchCategories = async () => {
    try {
      const res = await postRequest<{}, ApiResponse<Category[]>>("/Kategori/listele", {});
      if (res.success && res.data) setCategories(res.data);
    } catch {
      alert("Kategori yüklenirken hata oluştu.");
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const res = await postRequest<{}, ApiResponse<Product[]>>("/Urunler/listele", {});
      if (res.success && res.data) setProducts(res.data);
    } catch {
      alert("Ürünler yüklenirken hata oluştu.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchFeatures = async (urunId: number) => {
    try {
      const res = await postRequest<{}, ApiResponse<FeatureInput[]>>("/UrunOzellikleri/listele", {});
      if (res.success && res.data) {
        setFeatures(
          (res.data as any[])
            .filter((f) => f.urunId === urunId)
            .map((f) => ({
              id: f.id,
              ozellikAdi: f.ozellikAdi,
              ozellikDegeri: f.ozellikDegeri,
              siraNo: f.siraNo,
            }))
        );
      }
    } catch {
      alert("Özellikler yüklenirken hata oluştu.");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  /* ------ MODAL KONTROL ------ */
  const openAddModal = () => {
    setIsEditing(false);
    setForm({ ...emptyForm });
    setFeatures([]);
    setNewFiles([]);
    setExistingUrls([]);
    setRemovedUrls([]);
    setMainExistingUrl(null);
    setMainNewIndex(null);
    setImportUrl("");
    setModalOpen(true);
  };

  const openEditModal = (p: Product) => {
    setIsEditing(true);
    setForm({ ...p });

    let urls: string[] = Array.isArray(p.fotoUrls) && p.fotoUrls.length ? p.fotoUrls : [];
    const prof = p.profilFotoUrl;

    if (urls.length === 0) {
      const parsed = parseFotoField(p.fotoUrl);
      urls = parsed.list;
      if (!prof && parsed.main) p.profilFotoUrl = parsed.main;
    }

    setExistingUrls(urls);
    setNewFiles([]);
    setRemovedUrls([]);

    const mainCandidate = prof && urls.includes(prof) ? prof : urls[0] ?? null;
    setMainExistingUrl(mainCandidate || null);
    setMainNewIndex(null);
    setImportUrl("");

    fetchFeatures(p.id);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  /* ------ PriceCharting'ten İçe Aktar ------ */
  const importFromPriceCharting = async () => {
    const url = importUrl.trim();
    if (!url) return alert("Önce PriceCharting linkini yapıştırın.");
    if (!/pricecharting\.com/i.test(url)) return alert("Geçerli bir PriceCharting linki girin.");

    setImporting(true);
    try {
      const res = await postRequest<{ Url: string }, ApiResponse<PriceChartingResult>>(
        "/PriceCharting/cek",
        { Url: url }
      );
      if (!res.success || !res.data) throw new Error(res.errorDetail || "Bilgiler getirilemedi.");

      const d = res.data;

      // Ad, fiyat, para birimi, kur alanlarını doldur
      setForm((prev) => ({
        ...prev,
        ad: d.ad || prev.ad,
        fiyat: d.fiyatTl ?? d.fiyatUsd ?? prev.fiyat,
        paraBirimi: d.paraBirimi || prev.paraBirimi,
        kur: d.kur ?? prev.kur,
      }));

      // Görseli foto listesine ekle + profil yap
      if (d.fotoUrl) {
        setExistingUrls((prev) => (prev.includes(d.fotoUrl!) ? prev : [...prev, d.fotoUrl!]));
        setMainExistingUrl(d.fotoUrl);
        setMainNewIndex(null);
      }

      if (d.uyarilar && d.uyarilar.length) {
        alert("Bilgiler getirildi. Notlar:\n• " + d.uyarilar.join("\n• "));
      }
    } catch (e: any) {
      alert(e.message || "İçe aktarma sırasında hata oluştu.");
    } finally {
      setImporting(false);
    }
  };

  /* ------ ÖZELLİKLER ------ */
  const addFeatureRow = () =>
    setFeatures((prev) => [...prev, { ozellikAdi: "", ozellikDegeri: "", siraNo: prev.length + 1 }]);

  const removeFeatureRow = async (idx: number) => {
    const feat = features[idx];
    if (feat.id) {
      if (!window.confirm("Bu özelliği silmek istediğine emin misin?")) return;
      await postRequest<{ id: number }, ApiResponse<string>>("/UrunOzellikleri/sil", { id: feat.id });
    }
    setFeatures(features.filter((_, i) => i !== idx));
  };

  const updateFeature = (idx: number, field: keyof FeatureInput, value: string | number) => {
    const newList = [...features];
    // @ts-ignore
    newList[idx][field] = value;
    setFeatures(newList);
  };

  /* ------ FOTOĞRAFLAR ------ */
  const onFilesSelected = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    setNewFiles((prev) => {
      const merged = [...prev, ...arr];
      if (!mainExistingUrl && mainNewIndex === null && existingUrls.length === 0 && merged.length > 0) setMainNewIndex(0);
      return merged;
    });
  };

  const removeExistingUrl = (url: string) => {
    setExistingUrls((prev) => prev.filter((u) => u !== url));
    setRemovedUrls((prev) => [...prev, url]);

    if (mainExistingUrl === url) {
      const next = existingUrls.find((u) => u !== url) || null;
      if (next) {
        setMainExistingUrl(next);
        setMainNewIndex(null);
      } else if (newFiles.length > 0) {
        setMainExistingUrl(null);
        setMainNewIndex(0);
      } else {
        setMainExistingUrl(null);
        setMainNewIndex(null);
      }
    }
  };

  const removeNewFile = (idx: number) => {
    const next = [...newFiles];
    next.splice(idx, 1);
    setNewFiles(next);

    if (mainNewIndex === idx) {
      if (next.length > 0) setMainNewIndex(0);
      else if (existingUrls.length > 0) {
        setMainNewIndex(null);
        setMainExistingUrl(existingUrls[0]);
      } else {
        setMainNewIndex(null);
        setMainExistingUrl(null);
      }
    } else if (mainNewIndex !== null && mainNewIndex > idx) setMainNewIndex(mainNewIndex - 1);
  };

  const uploadPhotos = async (): Promise<string[]> => {
    if (!newFiles.length) return [];
    const fd = new FormData();
    newFiles.forEach((f) => fd.append("Dosyalar", f));
    const res = await postFormRequest<ApiResponse<{ yuklenenFotoUrlListesi: string[] }>>("/Fotograf/yukle", fd);
    if (!res.success || !res.data?.yuklenenFotoUrlListesi?.length) throw new Error(res.errorDetail || "Fotoğraf yükleme başarısız.");
    return res.data.yuklenenFotoUrlListesi;
  };

  const deleteRemovedPhotos = async () => {
    if (!removedUrls.length) return;
    try {
      await postRequest<{ UrlList: string[] }, ApiResponse<string[]>>("/Fotograf/sil", { UrlList: removedUrls });
    } catch {
      /* opsiyonel */
    }
  };

  /* ------ Hızlı Öne Çıkan Toggle ------ */
  const toggleFeatured = async (p: Product) => {
    try {
      const upd = await postRequest<any, ApiResponse<string>>("/Urunler/guncelle", {
        Id: p.id,
        KategoriId: p.kategoriId,
        Ad: p.ad,
        Aciklama: p.aciklama,
        Fiyat: p.fiyat,
        ParaBirimi: p.paraBirimi,
        StokAdedi: p.stokAdedi,
        AktifMi: p.aktifMi,

        ListeFiyati: p.listeFiyati ?? null,
        IndirimliFiyat: p.indirimliFiyat ?? null,
        IndirimBaslangic: p.indirimBaslangic ?? null,
        IndirimBitis: p.indirimBitis ?? null,
        AlisFiyati: p.alisFiyati ?? null,
        Kur: p.kur ?? null,

        OneCikanmi: !p.oneCikanmi,
        FotoUrl: p.fotoUrl ?? "",
      });

      if (!upd.success) throw new Error(upd.errorDetail || "Güncelleme başarısız");
      fetchProducts();
    } catch (e: any) {
      alert(e.message || "Öne çıkar güncelleme hatası.");
    }
  };

  /* ------ KAYDET ------ */
  const saveProduct = async () => {
    if (!form.ad?.trim()) return alert("Ürün adı zorunlu.");
    if (!form.kategoriId) return alert("Kategori seçmelisiniz.");

    try {
      const payloadBase = {
        KategoriId: form.kategoriId!,
        Ad: form.ad!.trim(),
        Aciklama: form.aciklama || "",

        Fiyat: safeNum(form.fiyat, 0),
        ParaBirimi: form.paraBirimi || "TRY",
        StokAdedi: safeNum(form.stokAdedi, 0),

        ListeFiyati: form.listeFiyati ?? null,
        IndirimliFiyat: form.indirimliFiyat ?? null,
        IndirimBaslangic: form.indirimBaslangic ?? null,
        IndirimBitis: form.indirimBitis ?? null,
        AlisFiyati: form.alisFiyati ?? null,
        Kur: form.kur ?? null,

        FotoUrl: form.fotoUrl || "",
        OneCikanmi: !!form.oneCikanmi,
      };

      let urunId: number;

      if (isEditing) {
        const res = await postRequest<any, ApiResponse<string>>("/Urunler/guncelle", {
          ...payloadBase,
          Id: form.id!,
          AktifMi: !!form.aktifMi,
        });
        if (!res.success) throw new Error(res.errorDetail);
        urunId = form.id!;
      } else {
        const res = await postRequest<any, ApiResponse<number>>("/Urunler/ekle", payloadBase);
        if (!res.success) throw new Error(res.errorDetail);
        urunId = res.data!;
      }

      const newlyUploaded = await uploadPhotos();
      const finalUrls = [...existingUrls, ...newlyUploaded];

      let finalMain = "";
      if (mainExistingUrl && finalUrls.includes(mainExistingUrl)) finalMain = mainExistingUrl;
      else if (mainNewIndex !== null && mainNewIndex >= 0 && mainNewIndex < newlyUploaded.length) finalMain = newlyUploaded[mainNewIndex];
      else finalMain = finalUrls[0] ?? "";

      const fotoUrlJson = JSON.stringify({ list: finalUrls, main: finalMain });

      const upd = await postRequest<any, ApiResponse<string>>("/Urunler/guncelle", {
        Id: urunId,
        ...payloadBase,
        AktifMi: isEditing ? !!form.aktifMi : true,
        FotoUrl: fotoUrlJson,
      });
      if (!upd.success) throw new Error(upd.errorDetail || "Güncelleme başarısız");

      await deleteRemovedPhotos();

      for (const feat of features) {
        if (!feat.ozellikAdi.trim()) continue;
        if (feat.id) {
          await postRequest<any, ApiResponse<string>>("/UrunOzellikleri/guncelle", {
            Id: feat.id,
            UrunId: urunId,
            OzellikAdi: feat.ozellikAdi,
            OzellikDegeri: feat.ozellikDegeri,
            SiraNo: feat.siraNo,
          });
        } else {
          await postRequest<any, ApiResponse<string>>("/UrunOzellikleri/ekle", {
            UrunId: urunId,
            OzellikAdi: feat.ozellikAdi,
            OzellikDegeri: feat.ozellikDegeri,
            SiraNo: feat.siraNo,
          });
        }
      }

      closeModal();
      fetchProducts();
    } catch (err: any) {
      alert(err.message || "Kaydetme sırasında hata oluştu.");
    }
  };

  const deleteProduct = async (id: number) => {
    if (!window.confirm("Ürünü silmek istediğine emin misin?")) return;
    try {
      const res = await postRequest<{ id: number }, ApiResponse<string>>("/Urunler/sil", { id });
      if (!res.success) throw new Error(res.errorDetail);
      fetchProducts();
    } catch (err: any) {
      alert(err.message || "Silme hatası.");
    }
  };

  const onDropzoneClick = () => document.getElementById("file-input-hidden")?.click();

  const renderPrice = (p: Product) => {
    const currency = p.paraBirimi || "TRY";
    const satis = typeof p.satisFiyati === "number" ? p.satisFiyati : undefined;
    const base = typeof p.listeFiyati === "number" ? p.listeFiyati : p.fiyat;

    const isDiscount = !!p.indirimAktifMi && typeof satis === "number" && satis !== base;

    if (!isDiscount) return <span className="font-semibold">{formatMoney(base, currency)}</span>;

    return (
      <div className="flex items-center gap-2">
        <span className="font-semibold">{formatMoney(satis!, currency)}</span>
        <span className="text-xs text-gray-500 line-through">{formatMoney(base, currency)}</span>
      </div>
    );
  };

  const previewSellPrice = () => {
    const currency = form.paraBirimi || "TRY";
    const base = typeof form.listeFiyati === "number" ? form.listeFiyati : safeNum(form.fiyat, 0);
    const disc = typeof form.indirimliFiyat === "number" ? form.indirimliFiyat : null;

    // burada sadece basit bir önizleme (tarih aralığı kontrolü yok)
    const sell = disc !== null ? disc : base;
    return { base, sell, currency, isDiscount: disc !== null && disc !== base };
  };

  /* =============================
     UI
  ============================= */
  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="bg-white border rounded-xl shadow-sm p-3 sm:p-3.5 md:p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-lg sm:text-xl md:text-2xl font-semibold tracking-tight">Ürün Yönetimi</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            Basit kullanım: Ürün adı + kategori + fiyat → Kaydet. Diğer her şey isteğe bağlı.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ürün ara…"
              className="pl-9 pr-3 py-2 border rounded-xl text-sm w-[240px] bg-white"
            />
          </div>
          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-2 bg-green-600 text-white px-3 sm:px-4 py-2 rounded-xl hover:bg-green-700 text-sm shadow-sm"
          >
            <Plus className="w-4 h-4" /> Yeni Ürün
          </button>
        </div>
      </div>

      {/* Mobil kart liste */}
      <div className="space-y-2 md:hidden">
        {loadingProducts ? (
          <div className="p-6 text-center text-gray-500">Yükleniyor…</div>
        ) : filteredProducts.length === 0 ? (
          <div className="p-6 text-center text-gray-500">Kayıt yok</div>
        ) : (
          filteredProducts.map((p) => {
            const parsed = parseFotoField(p.fotoUrl);
            const thumb = parsed.main || parsed.list[0] || "";
            return (
              <div key={p.id} className="bg-white border rounded-xl shadow-sm p-3">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
                    {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-gray-400">—</span>}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate flex items-center gap-1">
                      {p.ad}
                      {p.oneCikanmi && (
                        <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-lg bg-amber-100 text-amber-800 ml-1">
                          <Star className="w-3 h-3" /> Öne çıkan
                        </span>
                      )}
                      {p.indirimAktifMi && (
                        <span className="inline-flex items-center text-[11px] px-1.5 py-0.5 rounded-lg bg-rose-100 text-rose-700 ml-1">
                          İndirim
                        </span>
                      )}
                    </div>

                    <div className="text-xs text-gray-600">
                      {categories.find((c) => c.id === p.kategoriId)?.ad || "—"}
                    </div>

                    <div className="mt-1 text-sm flex items-center gap-2">
                      {renderPrice(p)}
                      <span className="text-gray-500">• Stok: {p.stokAdedi}</span>
                    </div>

                    <div className="mt-2 flex items-center gap-2">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          p.aktifMi ? "bg-green-100 text-green-800" : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {p.aktifMi ? "Aktif" : "Pasif"}
                      </span>

                      <button
                        onClick={() => toggleFeatured(p)}
                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-lg border ${
                          p.oneCikanmi ? "border-amber-300 bg-amber-50" : "hover:bg-gray-50"
                        }`}
                        title={p.oneCikanmi ? "Öne çıkanlardan kaldır" : "Öne çıkar"}
                      >
                        <Star className={`w-3 h-3 ${p.oneCikanmi ? "fill-amber-500 text-amber-500" : ""}`} />
                        {p.oneCikanmi ? "Kaldır" : "Öne çıkar"}
                      </button>
                    </div>
                  </div>

                  <div className="shrink-0 flex flex-col gap-1">
                    <button onClick={() => openEditModal(p)} className="p-2 border rounded-xl" title="Düzenle">
                      <Edit2 className="w-4 h-4 text-blue-600" />
                    </button>
                    <button onClick={() => deleteProduct(p.id)} className="p-2 border rounded-xl" title="Sil">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Masaüstü tablo */}
      <div className="bg-white border rounded-xl shadow-sm overflow-x-auto hidden md:block">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              {["Ad", "Kategori", "Fiyat", "Stok", "Durum", "Öne Çıkan", "Foto", "İşlemler"].map((h) => (
                <th key={h} className="px-3 py-2 text-left uppercase text-xs">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {loadingProducts ? (
              <tr>
                <td colSpan={8} className="p-6 text-center text-gray-500">
                  Yükleniyor…
                </td>
              </tr>
            ) : filteredProducts.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-6 text-center text-gray-500">
                  Kayıt yok
                </td>
              </tr>
            ) : (
              filteredProducts.map((p) => {
                const parsed = parseFotoField(p.fotoUrl);
                const thumb = parsed.main || parsed.list[0] || "";
                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-semibold">{p.ad}</td>
                    <td className="px-3 py-2">{categories.find((c) => c.id === p.kategoriId)?.ad || "—"}</td>
                    <td className="px-3 py-2">{renderPrice(p)}</td>
                    <td className="px-3 py-2">{p.stokAdedi}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          p.aktifMi ? "bg-green-100 text-green-800" : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {p.aktifMi ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => toggleFeatured(p)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${
                          p.oneCikanmi ? "border-amber-300 bg-amber-50" : "hover:bg-gray-50"
                        }`}
                        title={p.oneCikanmi ? "Öne çıkanlardan kaldır" : "Öne çıkar"}
                      >
                        <Star className={`w-4 h-4 ${p.oneCikanmi ? "fill-amber-500 text-amber-500" : ""}`} />
                        {p.oneCikanmi ? "Öne çıkan" : "Öne çıkar"}
                      </button>
                    </td>
                    <td className="px-3 py-2">{thumb ? <img src={thumb} alt="" className="w-10 h-10 object-cover rounded-lg" /> : "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditModal(p)} className="p-2 border rounded-xl hover:bg-white" title="Düzenle">
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </button>
                        <button onClick={() => deleteProduct(p.id)} className="p-2 border rounded-xl hover:bg-white" title="Sil">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={isEditing ? "Ürünü Düzenle" : "Yeni Ürün"}
        size="xxxxl"
        initialFocusRef={saveBtnRef as React.RefObject<HTMLElement>}
        footer={
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-gray-500">
              * Zorunlu alanlar: <b>Ürün Adı</b>, <b>Kategori</b>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={closeModal} className="px-4 py-2 rounded-xl border hover:bg-gray-100">
                Vazgeç
              </button>
              <button
                ref={saveBtnRef as unknown as React.RefObject<HTMLButtonElement>}
                onClick={saveProduct}
                className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 shadow-sm"
              >
                {isEditing ? "Güncelle" : "Kaydet"}
              </button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* PriceCharting'ten İçe Aktar */}
          <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
              <div className="flex items-center gap-2">
                <Download className="w-4 h-4 text-blue-700" />
                <span className="font-semibold text-sm text-blue-800">PriceCharting'ten İçe Aktar</span>
              </div>
              <span className="text-xs text-blue-600">Linki yapıştır, ad · fiyat · görsel otomatik dolsun.</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="url"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!importing) importFromPriceCharting();
                  }
                }}
                placeholder="https://www.pricecharting.com/game/..."
                className="flex-1 border rounded-xl px-3 py-2 bg-white text-sm"
              />
              <button
                type="button"
                onClick={importFromPriceCharting}
                disabled={importing}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 text-sm shadow-sm disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {importing ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Getiriliyor…
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" /> Bilgileri Getir
                  </>
                )}
              </button>
            </div>
            <div className="text-[11px] text-blue-700/80 mt-2">
              Fiyat: Ungraded değeri güncel USD/TRY kuru ile TL'ye çevrilir. Sonrasında elle düzenleyebilirsiniz.
            </div>
          </div>

          {/* Temel Bilgiler */}
          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">Temel Bilgiler</div>
                <div className="text-xs text-gray-500 mt-0.5">Burayı doldurmanız genellikle yeterli.</div>
              </div>

              {/* hızlı satış fiyatı önizleme */}
              <div className="text-right">
                {(() => {
                  const p = previewSellPrice();
                  return (
                    <>
                      <div className="text-xs text-gray-500">Satış fiyatı önizleme</div>
                      <div className="font-semibold">
                        {formatMoney(p.sell, p.currency)}
                        {p.isDiscount ? <span className="text-xs text-gray-500 line-through ml-2">{formatMoney(p.base, p.currency)}</span> : null}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Kategori */}
              <div>
                <label className="block text-sm font-medium">
                  Kategori <span className="text-red-500">*</span>
                </label>
                <Select<CategoryOption, false, GroupBase<CategoryOption>>
                  options={categoryOptions}
                  value={categoryOptions.find((o) => o.value === form.kategoriId) ?? null}
                  onChange={(opt) => setForm({ ...form, kategoriId: opt?.value ?? 0 })}
                  formatOptionLabel={(opt) => <div style={{ marginLeft: opt.depth * 12 }}>{opt.label}</div>}
                  isClearable
                  placeholder="Kategori ara veya seç…"
                  styles={{ menu: (base) => ({ ...base, zIndex: 60 }) }}
                />
              </div>

              {/* Ürün Adı */}
              <div>
                <label className="block text-sm font-medium">
                  Ürün Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.ad || ""}
                  onChange={(e) => setForm({ ...form, ad: e.target.value })}
                  placeholder="Örn: Samsung A54 256GB"
                  className="mt-1 w-full border rounded-xl px-3 py-2"
                />
              </div>

              {/* Fiyat */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:col-span-2">
                <div>
                  <label className="block text-sm font-medium">Fiyat</label>
                  <input
                    type="number"
                    value={form.fiyat ?? 0}
                    onChange={(e) => setForm({ ...form, fiyat: safeNum(e.target.value, 0) })}
                    placeholder="0"
                    className="mt-1 w-full border rounded-xl px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium">Para Birimi</label>
                  <select
                    value={form.paraBirimi || "TRY"}
                    onChange={(e) => setForm({ ...form, paraBirimi: e.target.value })}
                    className="mt-1 w-full border rounded-xl px-3 py-2 bg-white"
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium">Stok</label>
                  <input
                    type="number"
                    value={form.stokAdedi ?? 0}
                    onChange={(e) => setForm({ ...form, stokAdedi: safeNum(e.target.value, 0) })}
                    placeholder="0"
                    className="mt-1 w-full border rounded-xl px-3 py-2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Açıklama (Rich Text) */}
          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">Ürün Açıklaması</div>
                <div className="text-xs text-gray-500 mt-0.5">Kalın yazı, liste, link gibi düzenlemeler yapabilirsiniz.</div>
              </div>
              <span className="text-xs text-gray-400">Opsiyonel</span>
            </div>

            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <RichTextEditor
                  value={form.aciklama || ""}
                  onChange={(html) => setForm({ ...form, aciklama: html })}
                  placeholder="Örn: • Garanti: 24 ay  • Kutu içeriği: ...  • Öne çıkan özellikler: ..."
                />
                <div className="text-[11px] text-gray-500 mt-2">
                  İpucu: Madde işaretli liste ile yazarsanız müşteri çok daha rahat okur.
                </div>
              </div>

              <div>
                <div className="text-sm font-medium mb-2">Canlı Önizleme</div>
                <div className="rounded-xl border bg-gray-50 p-3 min-h-[170px]">
                  {form.aciklama?.trim() ? (
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: form.aciklama || "" }}
                    />
                  ) : (
                    <div className="text-sm text-gray-400">Önizleme burada görünecek…</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Gelişmiş Alanlar (kapalı) */}
          <Section
            title="Gelişmiş Fiyatlandırma & İndirim (isteğe bağlı)"
            desc="Liste fiyatı, indirimli fiyat, tarih aralığı, alış fiyatı ve kur gibi alanlar."
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium">Liste Fiyatı</label>
                <input
                  type="number"
                  value={form.listeFiyati ?? ""}
                  onChange={(e) => setForm({ ...form, listeFiyati: e.target.value === "" ? null : safeNum(e.target.value, 0) })}
                  placeholder="Boş bırakılabilir"
                  className="mt-1 w-full border rounded-xl px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">İndirimli Fiyat</label>
                <input
                  type="number"
                  value={form.indirimliFiyat ?? ""}
                  onChange={(e) => setForm({ ...form, indirimliFiyat: e.target.value === "" ? null : safeNum(e.target.value, 0) })}
                  placeholder="Boş bırakılabilir"
                  className="mt-1 w-full border rounded-xl px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Alış Fiyatı</label>
                <input
                  type="number"
                  value={form.alisFiyati ?? ""}
                  onChange={(e) => setForm({ ...form, alisFiyati: e.target.value === "" ? null : safeNum(e.target.value, 0) })}
                  placeholder="Boş bırakılabilir"
                  className="mt-1 w-full border rounded-xl px-3 py-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium">İndirim Başlangıç</label>
                <input
                  type="datetime-local"
                  value={toDatetimeLocalValue(form.indirimBaslangic)}
                  onChange={(e) => setForm({ ...form, indirimBaslangic: fromDatetimeLocalValue(e.target.value) })}
                  className="mt-1 w-full border rounded-xl px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">İndirim Bitiş</label>
                <input
                  type="datetime-local"
                  value={toDatetimeLocalValue(form.indirimBitis)}
                  onChange={(e) => setForm({ ...form, indirimBitis: fromDatetimeLocalValue(e.target.value) })}
                  className="mt-1 w-full border rounded-xl px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Kur</label>
                <input
                  type="number"
                  value={form.kur ?? ""}
                  onChange={(e) => setForm({ ...form, kur: e.target.value === "" ? null : safeNum(e.target.value, 0) })}
                  placeholder="Örn: 32.50"
                  className="mt-1 w-full border rounded-xl px-3 py-2"
                />
              </div>
            </div>

            <div className="mt-4 rounded-xl border bg-gray-50 p-3 text-xs text-gray-600">
              Satış fiyatı mantığı (backend): İndirim aktifse <b>İndirimliFiyat</b>, değilse <b>ListeFiyati/Fiyat</b>.
            </div>
          </Section>

          {/* Fotoğraflar */}
          <Section
            title="Fotoğraflar"
            desc="Görsel ekleyin, birini profil (ana foto) olarak işaretleyin."
            defaultOpen
          >
            <div className="space-y-3">
              {/* Dropzone */}
              <div
                onClick={onDropzoneClick}
                className="rounded-xl border-2 border-dashed border-gray-300 p-4 cursor-pointer hover:bg-gray-50"
              >
                <input
                  id="file-input-hidden"
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onFilesSelected(e.target.files)}
                />
                <div className="text-center text-gray-600 text-sm">
                  Görselleri sürükleyip bırakın veya tıklayıp seçin.
                </div>
                <div className="text-center text-xs text-gray-400 mt-1">
                  Toplam: {existingUrls.length + newFiles.length} görsel
                </div>
              </div>

              {/* Mevcut URL’ler */}
              {existingUrls.length > 0 && (
                <div>
                  <div className="text-sm font-medium">Kayıtlı fotoğraflar</div>
                  <div className="mt-2 grid grid-cols-3 md:grid-cols-6 gap-2">
                    {existingUrls.map((u) => (
                      <div key={u} className="relative w-24 h-24 rounded-xl overflow-hidden border shadow-sm">
                        <img src={u} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => {
                            setMainExistingUrl(u);
                            setMainNewIndex(null);
                          }}
                          className={`absolute bottom-1 left-1 rounded-lg px-1.5 py-0.5 text-[11px] flex items-center gap-1 ${
                            mainExistingUrl === u && mainNewIndex === null ? "bg-amber-100 text-amber-800" : "bg-white/90 text-gray-700 hover:bg-white"
                          }`}
                          title={mainExistingUrl === u && mainNewIndex === null ? "Profil fotoğrafı" : "Profil yap"}
                        >
                          <Star className="w-3 h-3" />
                          {mainExistingUrl === u && mainNewIndex === null ? "Profil" : "Profil"}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeExistingUrl(u)}
                          className="absolute top-1 right-1 bg-white/90 rounded-lg p-0.5 hover:bg-white"
                          title="Kaldır"
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Yeni seçilenler */}
              {newFiles.length > 0 && (
                <div>
                  <div className="text-sm font-medium">Yüklenecek fotoğraflar</div>
                  <div className="mt-2 grid grid-cols-3 md:grid-cols-6 gap-2">
                    {newFiles.map((f, i) => {
                      const preview = URL.createObjectURL(f);
                      const isMain = mainNewIndex === i && mainExistingUrl === null;
                      return (
                        <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border shadow-sm">
                          <img src={preview} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => {
                              setMainExistingUrl(null);
                              setMainNewIndex(i);
                            }}
                            className={`absolute bottom-1 left-1 rounded-lg px-1.5 py-0.5 text-[11px] flex items-center gap-1 ${
                              isMain ? "bg-amber-100 text-amber-800" : "bg-white/90 text-gray-700 hover:bg-white"
                            }`}
                            title={isMain ? "Profil (yükleme sonrası atanacak)" : "Profil yap"}
                          >
                            <Star className="w-3 h-3" />
                            {isMain ? "Profil" : "Profil"}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeNewFile(i)}
                            className="absolute top-1 right-1 bg-white/90 rounded-lg p-0.5 hover:bg-white"
                            title="Kaldır"
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Profil işaretli yeni fotoğraf, yükleme sonrası profil olarak atanır.</p>
                </div>
              )}

              <div className="text-xs text-gray-600">
                Profil fotoğrafı:{" "}
                <span className="font-medium">
                  {mainExistingUrl ? mainExistingUrl : mainNewIndex !== null ? `Yeni (#${mainNewIndex + 1})` : "(seçilmedi)"}
                </span>
              </div>
            </div>
          </Section>

          {/* Görünürlük */}
          <Section title="Görünürlük" desc="Satışta göster / Öne çıkanlarda göster" defaultOpen>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {isEditing && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form.aktifMi}
                    onChange={(e) => setForm({ ...form, aktifMi: e.target.checked })}
                  />
                  <span className="text-sm">Satışta göster</span>
                </label>
              )}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!form.oneCikanmi}
                  onChange={(e) => setForm({ ...form, oneCikanmi: e.target.checked })}
                />
                <span className="text-sm">Öne çıkanlarda göster</span>
              </label>
            </div>
          </Section>

          {/* Özellikler */}
          <Section title="Ürün Özellikleri (isteğe bağlı)" desc="Örn: Renk, Depolama, Ekran, Garanti…">
            <div className="flex justify-between items-center mb-2">
              <button
                type="button"
                onClick={addFeatureRow}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border hover:bg-gray-50 text-sm"
              >
                <Plus className="w-4 h-4" /> Satır Ekle
              </button>
              <div className="text-xs text-gray-500">Boş bırakabilirsiniz.</div>
            </div>

            {features.length === 0 ? (
              <p className="text-gray-500 text-sm">Henüz özellik yok.</p>
            ) : (
              <div className="space-y-2">
                {features.map((f, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end rounded-xl border p-3 bg-white">
                    <div className="md:col-span-5">
                      <label className="block text-xs font-medium">Başlık</label>
                      <input
                        type="text"
                        value={f.ozellikAdi}
                        onChange={(e) => updateFeature(idx, "ozellikAdi", e.target.value)}
                        placeholder="Örn: Renk"
                        className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-5">
                      <label className="block text-xs font-medium">Değer</label>
                      <input
                        type="text"
                        value={f.ozellikDegeri}
                        onChange={(e) => updateFeature(idx, "ozellikDegeri", e.target.value)}
                        placeholder="Örn: Siyah"
                        className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <label className="block text-xs font-medium">Sıra</label>
                      <input
                        type="number"
                        value={f.siraNo}
                        onChange={(e) => updateFeature(idx, "siraNo", safeNum(e.target.value, idx + 1))}
                        className="mt-1 w-full border rounded-xl px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeFeatureRow(idx)}
                        className="p-2 rounded-xl border hover:bg-rose-50"
                        title="Sil"
                      >
                        <X className="w-5 h-5 text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </Modal>
    </div>
  );
};

export default UrunlerPage;